'use client'

import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid'
import Image from 'next/image'
import { EffectFade, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/effect-fade'

export interface BEditorJsGalleryImage {
	url: string
	caption: string
}

export enum BEditorJsGalleryDisplayMode {
	MULTI = 'multi',
	SINGLE = 'single'
}

interface IUploadImage {
	name: string
	fileName: string
	collectionName: string
	size: number
	modelId: number
	modelType: string
	uuid: string
	id: number
	url: string
	originalFile: {
		id: string
		name: string
		path: string
		size: number
		modifiedAt: string
		createdAt: string
		updatedAt: string
		deletedAt: string | null
	}
}

export interface BEditorJsGalleryData {
	images: {
		image: IUploadImage
		caption: string
		alt?: string
	}[]
	displayMode: BEditorJsGalleryDisplayMode
}

export interface BEditorJsImageGalleryProps {
	data: BEditorJsGalleryData
}

export const BEditorJsImageGallery: React.FC<BEditorJsImageGalleryProps> = ({ data }) => {
	const { images, displayMode } = data

	// Only enable loop if there are enough images
	// For multi gallery with up to 3 slides per view, need at least 4 images
	// For single gallery with 1 slide per view, need at least 2 images
	const enableLoopMulti = images.length >= 4
	const enableLoopSingle = images.length >= 2

	const swiperButtonClasses =
		'cursor-pointer flex items-center justify-center w-10 h-10 rounded-full shadow border text-[#483577] border-[#483577] hover:bg-[#483577] hover:text-white transition-all duration-300'

	const renderMultiGallery = () => {
		return (
			<div className='relative editorjs-renderer_block'>
				<div className='w-editorblock mb-7'>
					<div className='flex justify-end'>
						<div className='flex gap-2'>
							<button className={`swiper-button-prev-multi ${swiperButtonClasses}`}>
								<ArrowLeftIcon className='w-4 h-4' />
							</button>

							<button className={`swiper-button-next-multi ${swiperButtonClasses}`}>
								<ArrowRightIcon className='w-4 h-4' />
							</button>
						</div>
					</div>
				</div>

				<div className='w-full relative'>
					<div className={`flex justify-center`}>
						<Swiper
							modules={[Navigation, EffectFade, Pagination]}
							navigation={{
								prevEl: '.swiper-button-prev-multi',
								nextEl: '.swiper-button-next-multi'
							}}
							pagination={false}
							spaceBetween={8}
							slidesPerView={1}
							breakpoints={{
								640: { slidesPerView: 1 },
								768: { slidesPerView: 2 },
								1024: { slidesPerView: 3 }
							}}
							centeredSlides={true}
							loop={enableLoopMulti}
							className='w-editorblock w-full'
						>
							{images.map((image, index) => (
								<SwiperSlide
									key={index}
									style={{ height: '215px', width: '215px' }}
								>
									<figure className='h-full w-full rounded-[12px] aspect-square'>
										<div className='h-full w-full rounded-[12px] aspect-square bg-[#DED3B9]'>
											<Image
												unoptimized={true}
												src={
													image?.image?.url ||
													`${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/placeholder-image.webp`
												}
												alt={image?.alt || image?.caption || 'Image'}
												className={
													'h-full w-full object-cover rounded-[12px] aspect-square'
												}
												loading='lazy'
												height={215}
												width={215}
											/>
										</div>
										{image.caption && (
											<figcaption className='mt-1 text-sm text-[#483577] font-light'>
												{image.caption}
											</figcaption>
										)}
									</figure>
								</SwiperSlide>
							))}
						</Swiper>
					</div>
				</div>
			</div>
		)
	}

	const renderSingleGallery = () => {
		return (
			<div className='relative editorjs-renderer_block'>
				<div
					className={`swiper-container-wrapper w-screen relative left-[50%] right-[50%] mx-[-50vw]`}
				>
					<div className='mx-auto'>
						<Swiper
							modules={[Navigation, Pagination]}
							navigation={{
								prevEl: '.swiper-button-prev-single',
								nextEl: '.swiper-button-next-single'
							}}
							pagination={{
								el: '.swiper-pagination-single',
								type: 'fraction',
								formatFractionCurrent: number => `${number}`,
								formatFractionTotal: number => `${number}`,
								renderFraction: (currentClass, totalClass) => {
									return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
								}
							}}
							slidesPerView={1}
							loop={enableLoopSingle}
							centeredSlides={true}
						>
							{images.map((image, index) => (
								<SwiperSlide key={index}>
									<div className='flex justify-center'>
										<figure className='relative w-editorblock'>
											<div className='w-full h-auto rounded-[21px] aspect-[658/403] bg-[#DED3B9]'>
												<Image
													unoptimized={true}
													src={
														image?.image?.url ||
														`${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/placeholder-image.webp`
													}
													alt={image.alt || image.caption || 'Image'}
													className='w-full h-auto rounded-[21px] aspect-[658/403] object-cover'
													loading='lazy'
													height={403}
													width={658}
												/>
											</div>
											{image.caption && (
												<figcaption className='mt-4 text-sm text-[#483577] font-light'>
													{image.caption}
												</figcaption>
											)}
										</figure>
									</div>
								</SwiperSlide>
							))}
						</Swiper>
					</div>
				</div>

				<div className='flex justify-between items-center mt-7 w-editorblock'>
					<div
						className='swiper-pagination-single text-sm font-light'
						style={{ color: '#483577' }}
					></div>
					<div className='flex gap-2'>
						<button className={`swiper-button-prev-single ${swiperButtonClasses}`}>
							<ArrowLeftIcon className='w-4 h-4' />
						</button>
						<button className={`swiper-button-next-single ${swiperButtonClasses}`}>
							<ArrowRightIcon className='w-4 h-4' />
						</button>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className='mx-auto px-4  mt-12 mb-14'>
			{displayMode === BEditorJsGalleryDisplayMode.MULTI
				? renderMultiGallery()
				: renderSingleGallery()}
		</div>
	)
}

export default BEditorJsImageGallery
